import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import type { Strain } from '../../types/strain';
import ImageUpload from '../ui/ImageUpload';
import {
    X,
    Activity,
    Dna,
    Wind,
    Brain,
    Zap,
    Leaf,
    Share2,
    Bookmark,
    Info,
    Grape,
    Citrus,
    TreePine,
    Droplets,
    Plus,
    Camera,
    ChevronLeft,
    ChevronRight,
    ImageIcon
} from 'lucide-react';

interface StrainDetailsModalProps {
    strain: Strain | null;
    onClose: () => void;
}

type Tab = 'overview' | 'genetics' | 'effects';

interface StrainImage {
    id: string;
    storage_path: string;
    is_primary: boolean;
}

export default function StrainDetailsModal({ strain, onClose }: StrainDetailsModalProps) {
    const [activeTab, setActiveTab] = useState<Tab>('overview');
    const [images, setImages] = useState<StrainImage[]>([]);
    const [loadingImages, setLoadingImages] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        if (strain) {
            fetchImages();
            setShowUpload(false);
            setCurrentImageIndex(0);
        }
    }, [strain]);

    const fetchImages = async () => {
        if (!strain) return;
        setLoadingImages(true);
        const { data, error } = await supabase
            .from('strain_images')
            .select('*')
            .eq('strain_id', strain.id)
            .order('created_at', { ascending: false });

        if (!error && data) {
            setImages(data);
        }
        setLoadingImages(false);
    };

    const getPublicUrl = (path: string) => {
        const { data } = supabase.storage.from('strain-images').getPublicUrl(path);
        return data.publicUrl;
    };

    const handleNextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    if (!strain) return null;

    const typeColors = {
        indica: 'from-purple-500/80 to-indigo-600/80 text-purple-100',
        sativa: 'from-amber-400/80 to-orange-600/80 text-amber-100',
        hybrid: 'from-emerald-400/80 to-teal-600/80 text-emerald-100',
        unknown: 'from-slate-500/80 to-gray-600/80 text-slate-100',
    };

    // Placeholder data extraction (simulate rich data from notes if needed in future)
    // For now, we build the robust UI buckets.
    const effects = [
        { name: 'Relaxed', icon: Wind, score: 85 },
        { name: 'Happy', icon: Brain, score: 72 },
        { name: 'Euphoric', icon: Zap, score: 68 },
    ];

    const flavors = [
        { name: 'Berry', icon: Grape },
        { name: 'Citrus', icon: Citrus },
        { name: 'Earthy', icon: TreePine },
    ];

    // Determine Hero Background
    const hasImages = images.length > 0;
    const currentImageUrl = hasImages ? getPublicUrl(images[currentImageIndex].storage_path) : null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in duration-300">
            <div
                className="card w-full max-w-2xl bg-dark-bg/95 border-dark-border shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300 rounded-[32px]"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Hero Header / Gallery */}
                <div className={`relative h-64 bg-gradient-to-br ${typeColors[strain.strain_type]} flex items-end p-8 overflow-hidden shrink-0 group`}>

                    {/* Loading State */}
                    {loadingImages && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                            <div className="animate-spin text-white">
                                <Activity size={24} />
                            </div>
                        </div>
                    )}

                    {/* Image Carousel */}
                    {hasImages && !showUpload && (
                        <>
                            <div className="absolute inset-0 bg-black/20 z-0">
                                <img
                                    src={currentImageUrl!}
                                    alt={strain.name}
                                    className="w-full h-full object-cover opacity-80 mix-blend-overlay"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-transparent to-transparent" />
                            </div>

                            {/* Carousel Controls */}
                            {images.length > 1 && (
                                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                    <button onClick={handlePrevImage} className="p-2 rounded-full bg-black/40 text-white backdrop-blur-md hover:bg-black/60 transition-colors">
                                        <ChevronLeft size={20} />
                                    </button>
                                    <button onClick={handleNextImage} className="p-2 rounded-full bg-black/40 text-white backdrop-blur-md hover:bg-black/60 transition-colors">
                                        <ChevronRight size={20} />
                                    </button>
                                </div>
                            )}

                            {/* Image Counter */}
                            {images.length > 1 && (
                                <div className="absolute top-4 left-4 z-20 px-3 py-1 bg-black/40 backdrop-blur-md rounded-full text-[10px] font-bold text-white uppercase tracking-widest border border-white/10">
                                    {currentImageIndex + 1} / {images.length}
                                </div>
                            )}
                        </>
                    )}

                    {/* Default Abstract Background (if no images) */}
                    {!hasImages && !showUpload && (
                        <>
                            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
                            <div className="absolute bottom-0 left-0 w-40 h-40 bg-black/10 rounded-full blur-2xl translate-y-1/3 -translate-x-1/3 pointer-events-none" />
                        </>
                    )}

                    {/* Header Controls */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 backdrop-blur-md rounded-full text-white transition-all z-30"
                    >
                        <X size={20} />
                    </button>

                    <div className="relative z-10 w-full flex justify-between items-end">
                        <div className={showUpload ? 'hidden' : 'block'}>
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-full bg-black/30 backdrop-blur-md border border-white/10 ${typeColors[strain.strain_type]}`}>
                                    {strain.strain_type}
                                </span>
                                {strain.default_thc && (
                                    <span className="text-[10px] font-black uppercase tracking-widest text-white/80 bg-black/20 px-2 py-1 rounded-full flex items-center gap-1">
                                        <Zap size={10} className="fill-current" /> {strain.default_thc}% THC
                                    </span>
                                )}
                            </div>
                            <h2 className="text-4xl font-black tracking-tighter text-white drop-shadow-lg">{strain.name}</h2>
                            <p className="text-white/80 font-medium text-sm mt-1 flex items-center gap-1">
                                <Dna size={14} /> {strain.genetics || 'Unknown Lineage'}
                            </p>
                        </div>

                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowUpload(!showUpload)}
                                className={`p-3 backdrop-blur-md rounded-2xl border transition-all active:scale-95 ${showUpload ? 'bg-accent-purple text-white border-accent-purple' : 'bg-white/10 hover:bg-white/20 border-white/10 text-white'}`}
                            >
                                {showUpload ? <ImageIcon size={18} /> : <Camera size={18} />}
                            </button>
                            <button className="p-3 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-2xl border border-white/10 text-white transition-all active:scale-95">
                                <Share2 size={18} />
                            </button>
                            <button className="p-3 bg-white text-accent-purple rounded-2xl font-black shadow-lg shadow-black/20 active:scale-95 transition-all text-xs flex items-center gap-2">
                                <Bookmark size={16} /> Save
                            </button>
                        </div>
                    </div>

                    {/* Upload Modal Overlay */}
                    {showUpload && (
                        <div className="absolute inset-0 bg-dark-bg z-20 p-6 flex flex-col justify-center animate-in fade-in duration-200">
                            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4 flex items-center gap-2">
                                <Camera size={16} /> Contribute Evidence
                            </h3>
                            <ImageUpload
                                strainId={strain.id}
                                onUploadComplete={() => {
                                    fetchImages();
                                    setShowUpload(false);
                                }}
                            />
                            <button
                                onClick={() => setShowUpload(false)}
                                className="mt-4 text-[10px] font-bold text-dark-muted uppercase tracking-widest hover:text-white transition-colors self-center"
                            >
                                Cancel Upload
                            </button>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex items-center px-6 pt-2 border-b border-dark-border/50 bg-dark-surface/30 shrink-0 overflow-x-auto scrollbar-hide">
                    {(['overview', 'genetics', 'effects'] as Tab[]).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-4 text-xs font-black uppercase tracking-[0.15em] border-b-2 transition-all whitespace-nowrap ${activeTab === tab
                                ? 'border-accent-purple text-accent-purple'
                                : 'border-transparent text-dark-muted hover:text-white'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* Content Area */}
                <div className="p-8 overflow-y-auto custom-scrollbar flex-1 relative">

                    {activeTab === 'overview' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-5 bg-dark-bg/60 rounded-3xl border border-dark-border/60">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Activity size={16} className="text-accent-green" />
                                        <h3 className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Potency</h3>
                                    </div>
                                    <p className="text-3xl font-black text-white tracking-tight">
                                        {strain.default_thc ? `${strain.default_thc}%` : '--'}
                                        <span className="text-sm text-dark-muted ml-1 font-bold">THC</span>
                                    </p>
                                    <div className="w-full h-1.5 bg-dark-bg mt-3 rounded-full overflow-hidden">
                                        <div className="h-full bg-accent-green rounded-full" style={{ width: `${(strain.default_thc || 0) * 2.5}%` }} />
                                    </div>
                                </div>
                                <div className="p-5 bg-dark-bg/60 rounded-3xl border border-dark-border/60">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Leaf size={16} className="text-accent-purple" />
                                        <h3 className="text-[10px] font-black text-dark-muted uppercase tracking-widest">Type</h3>
                                    </div>
                                    <p className="text-xl font-black text-white capitalize">{strain.strain_type}</p>
                                    <p className="text-[10px] text-dark-muted mt-1 leading-tight">
                                        {strain.strain_type === 'sativa' ? 'Cerebral & Uplifting' :
                                            strain.strain_type === 'indica' ? 'Physical & Relaxing' : 'Balanced Hybrid'}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <h3 className="flex items-center gap-2 text-xs font-black text-dark-muted uppercase tracking-[0.2em] mb-4">
                                    <Info size={14} /> Architect's Notes
                                </h3>
                                <div className="p-6 bg-dark-surface/40 rounded-3xl border border-dark-border/40 text-sm leading-relaxed text-dark-text/90 font-medium">
                                    {strain.notes ? strain.notes : (
                                        <span className="text-dark-muted italic">No curatorial notes available for this specimen yet.</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'effects' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div>
                                <h3 className="text-[10px] font-black text-dark-muted uppercase tracking-widest mb-4">Reported Effects</h3>
                                <div className="grid grid-cols-1 gap-3">
                                    {effects.map((effect) => (
                                        <div key={effect.name} className="flex items-center gap-4 p-3 bg-dark-bg/40 rounded-2xl border border-white/5">
                                            <div className="p-2 bg-dark-surface rounded-xl text-accent-purple">
                                                <effect.icon size={18} />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center mb-1.5">
                                                    <span className="text-xs font-bold text-white uppercase tracking-wider">{effect.name}</span>
                                                    <span className="text-[10px] font-bold text-dark-muted">{effect.score}%</span>
                                                </div>
                                                <div className="h-1.5 bg-dark-bg rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-accent-purple/40 to-accent-purple rounded-full"
                                                        style={{ width: `${effect.score}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="text-[10px] font-black text-dark-muted uppercase tracking-widest mb-4">Flavor Profile</h3>
                                <div className="flex flex-wrap gap-2">
                                    {flavors.map((flavor) => (
                                        <span key={flavor.name} className="px-4 py-2 bg-dark-bg border border-white/5 rounded-xl text-xs font-bold text-dark-muted flex items-center gap-2 hover:border-accent-purple/30 hover:text-white transition-all">
                                            <flavor.icon size={14} className="text-accent-purple/70" />
                                            {flavor.name}
                                        </span>
                                    ))}
                                    <span className="px-4 py-2 bg-dark-bg border border-white/5 border-dashed rounded-xl text-xs font-bold text-dark-muted/40 flex items-center gap-2">
                                        <Plus size={14} /> Add Tag
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'genetics' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                            <div className="p-6 bg-gradient-to-br from-dark-surface/50 to-dark-bg rounded-[32px] border border-dark-border text-center">
                                <Dna size={48} className="text-accent-purple mx-auto mb-4 opacity-50" />
                                <p className="text-[10px] font-black text-dark-muted uppercase tracking-widest mb-2">Primary Lineage</p>
                                <h2 className="text-2xl font-black text-white tracking-tight mb-6">
                                    {strain.genetics || 'Unknown Heritage'}
                                </h2>

                                <div className="flex items-center justify-center gap-4 text-xs font-bold text-dark-muted opacity-60">
                                    <span className="px-4 py-2 rounded-xl bg-dark-bg border border-white/5">Parent A</span>
                                    <X size={12} />
                                    <span className="px-4 py-2 rounded-xl bg-dark-bg border border-white/5">Parent B</span>
                                </div>
                            </div>

                            <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 flex gap-4">
                                <div className="p-2 bg-amber-500/10 rounded-xl shrink-0 h-fit">
                                    <Droplets size={18} className="text-amber-500" />
                                </div>
                                <div>
                                    <h4 className="text-xs font-black text-amber-500 uppercase tracking-widest mb-1">Terpene Profile</h4>
                                    <p className="text-[11px] text-amber-200/60 leading-relaxed font-medium">
                                        Terpene data is currently unavailable for this specimen. Connect with a lab partner to populate chemotype data.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* Backdrop click to close */}
            <div className="absolute inset-0 -z-10" onClick={onClose} />
        </div>
    );
}
